/**
 * Lenavio Connect — Microsoft Teams integration card.
 *
 * Mirrors ``SlackCard``:
 *   1. Plan-locked (no ``teams_integration``) → upsell.
 *   2. Not connected (or previously disconnected) → "Connect to Teams".
 *   3. Connected → tenant name + slash-command help + "Disconnect".
 *
 * Note: the backend's install endpoint returns a placeholder URL
 * when ``TEAMS_CLIENT_ID`` isn't configured server-side. The card
 * detects that case and renders a "credentials pending" hint
 * instead of redirecting to a stub URL.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AtSign,
  Bell,
  Check,
  Lock,
  MessageSquare,
  Sparkles,
} from "lucide-react";

import {
  disconnectTeams,
  getTeamsInstallUrl,
  getTeamsStatus,
  setTeamsWebhook,
  type TeamsStatusResponse,
} from "@/api/integrationsTeams";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { useToast } from "@/hooks/useToast";

export function TeamsCard() {
  const { features, has, loading: featuresLoading } = usePlanFeatures();
  const planLocked = features != null && !has("teams_integration");
  const { addToast } = useToast();
  const [params, setParams] = useSearchParams();

  const [status, setStatus] = useState<TeamsStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [webhookDraft, setWebhookDraft] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);

  const refresh = useCallback(async () => {
    if (planLocked) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setStatus(await getTeamsStatus());
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [planLocked]);

  useEffect(() => {
    if (featuresLoading) return;
    void refresh();
  }, [refresh, featuresLoading]);

  // Surface OAuth-callback redirect-back state. The backend
  // returns ?teams=connected on success.
  useEffect(() => {
    const ok = params.get("teams");
    const err = params.get("teams_error");
    if (ok === "connected") {
      addToast("success", "Microsoft Teams connected!");
      params.delete("teams");
      setParams(params, { replace: true });
      void refresh();
    } else if (err) {
      addToast("error", `Teams connection failed: ${err}`);
      params.delete("teams_error");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setInstalling(true);
    try {
      const res = await getTeamsInstallUrl();
      // Detect the placeholder URL the backend returns when
      // TEAMS_CLIENT_ID isn't wired up — surface a helpful toast
      // instead of bouncing the user to a settings stub.
      if (res.authorize_url.includes("teams_setup=pending")) {
        addToast(
          "info",
          "Teams app credentials aren't configured on the server yet. Ask your admin to set TEAMS_CLIENT_ID + TEAMS_CLIENT_SECRET.",
        );
        setInstalling(false);
        return;
      }
      window.location.href = res.authorize_url;
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not start Teams install.";
      addToast("error", detail);
      setInstalling(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await disconnectTeams();
      setStatus(res);
      addToast("success", "Microsoft Teams disconnected.");
    } catch {
      addToast("error", "Could not disconnect Teams.");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveWebhook = async (clear = false) => {
    setSavingWebhook(true);
    try {
      const res = await setTeamsWebhook(clear ? "" : webhookDraft.trim());
      setStatus(res);
      setWebhookDraft("");
      addToast(
        "success",
        clear
          ? "Teams webhook removed."
          : "Teams webhook saved — notifications will post to your channel.",
      );
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not save the webhook URL.";
      addToast("error", detail);
    } finally {
      setSavingWebhook(false);
    }
  };

  return (
    <Card className={planLocked ? "opacity-80" : undefined}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent/10 p-2">
            <MessageSquare className="h-5 w-5 text-accent" aria-hidden />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-heading text-text-primary">
              Microsoft Teams
            </h3>
            <p className="text-[11px] uppercase tracking-wider text-text-muted">
              Communication
            </p>
          </div>
        </div>
        {!planLocked && status?.connected && (
          <Badge variant="success">Connected</Badge>
        )}
        {!planLocked && status && !status.connected && status.ever_installed && (
          <Badge variant="warning">Disconnected</Badge>
        )}
        {!planLocked && status && !status.connected && !status.ever_installed && (
          <Badge variant="default">Not connected</Badge>
        )}
        {planLocked && (
          <Badge variant="default" className="uppercase tracking-wide">
            Enterprise
          </Badge>
        )}
      </div>

      {/* Plan-locked */}
      {planLocked && (
        <>
          <p className="text-sm text-text-muted mb-4">
            Let your team check PTO, file leave requests, and approve them
            with adaptive cards directly inside Microsoft Teams.
          </p>
          <div className="rounded-lg border border-dashed border-border bg-background p-4 text-sm flex items-start gap-3">
            <Lock
              className="h-4 w-4 mt-0.5 text-text-muted shrink-0"
              aria-hidden
            />
            <div>
              <p className="text-text-primary font-medium">
                Upgrade to Enterprise
              </p>
              <p className="text-text-muted">
                Teams integration is available on Enterprise plans (and
                during your trial).
              </p>
            </div>
          </div>
          <div className="mt-4">
            <a
              href="/settings/billing"
              className="inline-flex items-center gap-2 rounded-lg border border-accent bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent/90"
            >
              <Sparkles className="h-4 w-4" />
              View plans
            </a>
          </div>
        </>
      )}

      {/* Loading */}
      {!planLocked && loading && (
        <div className="h-12 rounded bg-border/30 animate-pulse" />
      )}

      {/* Incoming Webhook — outbound notifications (independent of the
          bot). This is the "simpler approach first" path: no Azure AD,
          no app install — paste a channel webhook and Lenavio posts
          notifications to it. Always shown (not gated on bot state). */}
      {!planLocked && !loading && status && (
        <div className="rounded-lg border border-border bg-surface p-3 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="h-4 w-4 text-accent" aria-hidden />
            <p className="text-sm text-text-primary font-medium">
              Channel notifications
            </p>
            {status.webhook_configured && (
              <Badge variant="success" className="ml-auto">
                Active
              </Badge>
            )}
          </div>
          <p className="text-xs text-text-muted mb-2">
            Post new hires, leave decisions, signed documents,
            announcements, and Atlas / Sentinel alerts to a Teams
            channel. In Teams: channel <strong>···</strong> menu →{" "}
            <em>Connectors</em> → <em>Incoming Webhook</em> → create one,
            copy the URL, paste it here.
          </p>
          {status.webhook_configured ? (
            <>
              <p className="text-[11px] text-success mb-2">
                <Check className="inline h-3 w-3 -mt-0.5 mr-0.5" aria-hidden />
                A webhook is configured. Paste a new URL to replace it.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  value={webhookDraft}
                  onChange={(e) => setWebhookDraft(e.target.value)}
                  placeholder="https://….webhook.office.com/…"
                  className="flex-1"
                  type="url"
                />
                <Button
                  size="sm"
                  onClick={() => handleSaveWebhook(false)}
                  isLoading={savingWebhook}
                  disabled={!webhookDraft.trim()}
                >
                  Replace
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSaveWebhook(true)}
                  isLoading={savingWebhook}
                >
                  Remove
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                value={webhookDraft}
                onChange={(e) => setWebhookDraft(e.target.value)}
                placeholder="https://….webhook.office.com/…"
                className="flex-1"
                type="url"
              />
              <Button
                size="sm"
                onClick={() => handleSaveWebhook(false)}
                isLoading={savingWebhook}
                disabled={!webhookDraft.trim()}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Connected */}
      {!planLocked && !loading && status?.connected && (
        <>
          <p className="text-sm mb-2 text-text-primary">
            <Check
              className="inline h-4 w-4 -mt-0.5 mr-1 text-success"
              aria-hidden
            />
            Connected to{" "}
            <strong>
              {status.tenant_name || "Microsoft Teams"}
            </strong>
          </p>

          <div className="rounded-lg border border-dashed border-border bg-background p-3 text-xs text-text-muted space-y-1.5 mb-4">
            <p className="text-text-primary font-medium">Bot commands</p>
            <p>
              Your team can mention <code>@lenavio</code> in any Teams
              channel or 1:1 chat:
            </p>
            <ul className="space-y-0.5 ml-4">
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">
                  @lenavio pto
                </code>{" "}
                — check PTO balance
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">
                  @lenavio request
                </code>{" "}
                — request time off
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">
                  @lenavio directory
                </code>{" "}
                — search the directory
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">
                  @lenavio approve
                </code>{" "}
                — pending approvals (managers)
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">
                  @lenavio status
                </code>{" "}
                — your recent requests
              </li>
            </ul>
            <p className="pt-1">
              Managers receive adaptive cards with Approve / Deny buttons
              when a direct report submits a leave request.
            </p>
          </div>

          <Button
            size="sm"
            variant="ghost"
            onClick={handleDisconnect}
            isLoading={disconnecting}
          >
            Disconnect
          </Button>
        </>
      )}

      {/* Not connected — the BOT (optional, for @lenavio commands).
          Notifications work via the webhook above without this. */}
      {!planLocked && !loading && status && !status.connected && (
        <>
          <p className="text-sm text-text-muted mb-1">
            <span className="font-medium text-text-primary">
              Optional: connect the bot
            </span>{" "}
            so your team can <code>@lenavio</code> for PTO, leave
            requests, and approvals inside Teams. Notifications above
            don&rsquo;t require this.
          </p>
          <Button
            onClick={handleConnect}
            isLoading={installing}
            className="mt-3"
          >
            <AtSign className="h-4 w-4" />
            Connect to Teams
          </Button>
        </>
      )}
    </Card>
  );
}
